import Decimal from "decimal.js";

import { D0 } from "../constants";
import type { VectorD3 } from "../vector";
import { ORIGIN, transform2DVector, transform2DVectors } from "../vector";
import { Box, WCollider } from "./collider";

export class WCompositeCollider extends WCollider {

  public colliders: WCollider[];
  private localLocations: VectorD3[];
  private locations: VectorD3[];

 

  constructor(
    public localColliders: WCollider[],
    location = ORIGIN,
    rotation = D0
  ) {
    super(location, rotation);

    this.colliders = [];
    this.localLocations = [];
    this.locations = [];

    for(const c of localColliders) {
      // This should never happen; in case it happens there are two options
      // 1. decompose the composite child collider, if this is called with an hard coded colliders set
      // 2. refactor this constructor to correctly handle the case, if this is called with a programmatically composed colliders set
      if(c instanceof WCompositeCollider) throw new Error("Nested WCompositeCollider");

      this.createColliderFromLocal(c);
    }
  }

  createColliderFromLocal(c: WCollider) {
    this.localLocations.push(c.location);
    const location = transform2DVector(c.location, this.location, this.rotation);
    this.locations.push(location);
    const worldCollider = c.clone();
    worldCollider.setLocationAndRotation(location, this.rotation);
    this.colliders.push(worldCollider);
  }

  addLocalCollider(c: WCollider) {
    this.localColliders.push(c);
    this.createColliderFromLocal(c);
  }

  distanceTo(_collider: WCollider): Decimal {
    throw new Error("Method not implemented.");
  }

  getBox(): Box {
    let box = this.colliders[0].getBox();
    let left = box.left;
    let right = box.right;
    let top = box.top;
    let bottom = box.bottom;

    // expand the box by other colliders boxes
    for(let i = 1; i < this.colliders.length; ++i) {
      box = this.colliders[i].getBox();
      left = Decimal.min(left, box.left);
      right = Decimal.max(right, box.right);
      top = Decimal.min(top, box.top);
      bottom = Decimal.max(bottom, box.bottom);
    }

    return new Box(left, right, top, bottom);
  }

  createExpanded(_distance: Decimal): WCollider {
    const localColliders = [];
    for(const c of this.localColliders)
      localColliders.push(c.createExpanded(_distance));

    return new WCompositeCollider(localColliders, this.location, this.rotation);
  }

  getPoints(): VectorD3[] {
    throw new Error("Method not implemented.");
  }

  getLocalPoints(): VectorD3[] {
    throw new Error("Method not implemented.");
  }

  // Misc

  clone(): WCollider {
    return new WCompositeCollider(this.localColliders, this.location, this.rotation);
  }

  // Position

  updatePoints(): void {
    const { colliders, rotation } = this;
    const locations = transform2DVectors(this.localLocations, this.location, rotation);

    for(let i = 0; i < colliders.length; ++i) colliders[i].setLocationAndRotation(locations[i], rotation);
  }

  // Collisions

  // THIS IS NOT CORRECT, but for our use case should be enough
  // as we should use this only to check is a model is fully within a given distance from
  // another entity which means to check if a small container is contained in a large container
  contains(collider: WCollider): boolean {
    if(collider instanceof WCompositeCollider) {
      for(const c of this.colliders) if(collider.isContained(c)) return true;
    } else for(const c of this.colliders) if(c.contains(collider)) return true;

    return false;
  }

  isContained(collider: WCollider): boolean {
    if(collider instanceof WCompositeCollider) return collider.contains(this);
    else {
      for(const c of this.colliders) if(! c.isContained(collider)) return false;

      return true;
    }
  }

  overlaps(collider: WCollider): boolean {
    if(collider instanceof WCompositeCollider) {
      const { colliders } = collider;

      for(const c1 of this.colliders) for(const c2 of colliders) if(c1.overlaps(c2)) return true;
    } else for(const c of this.colliders) if(c.overlaps(collider)) return true;

    return false;
  }
}
